import {
  Box,
  Heading,
  VStack,
  Button,
  Link as ChakraLink,
  Flex,
  Modal,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  Center,
} from "@chakra-ui/react";
import {
  useGetGoRulesObjects,
  useMe,
  useS3Client,
  useSaveS3File,
  useGetS3Object,
} from "../../components/api";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaPlus as PlusIcon } from "react-icons/fa";
import { useState, useEffect, useMemo } from "react";
import "@gorules/jdm-editor/dist/style.css";
import {
  DecisionGraph,
  JdmConfigProvider,
  type DecisionGraphType,
} from "@gorules/jdm-editor";
import { useAtom } from "jotai";
import { page_title } from "../../components/global_state";
import { debounce } from "lodash";

const GoRules = () => {
  const set_page_title = useAtom(page_title)[1];
  const navigate = useNavigate();
  const { "*": route_suffix = "" } = useParams();
  const { data: user } = useMe();
  const { data: s3_access } = useS3Client(user?.jwt);
  const { data: gorules_objs } = useGetGoRulesObjects(s3_access?.s3_client);
  //const { data: gorules_objs } = useGetGoRulesObjects();

  const [graph, set_graph] = useState<null | DecisionGraphType>(null);
  const [graph_dirty, set_graph_dirty] = useState(false);
  const [show_new_name_modal, set_show_new_name_modal] = useState(false);
  const [new_name, set_new_name] = useState<undefined | string>(undefined);

  const [load_complete, set_load_complete] = useState(false);

  const debounced_set_load_complete = useMemo(
    () => debounce(() => set_load_complete(true), 300),
    [set_load_complete],
  );
  useEffect(
    () => debounced_set_load_complete(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [route_suffix],
  );

  const { data: storedRules } = useGetS3Object(
    s3_access?.s3_client,
    "erp-gorules",
    route_suffix && route_suffix !== "new" ? route_suffix : undefined,
  );

  useEffect(() => {
    (async () => {
      switch (route_suffix) {
        case "":
          set_graph(null);
          break;
        case "new":
          set_graph({ nodes: [], edges: [] });
          break;
        default: {
          // TODO fetch
          if (storedRules) {
            const stored_graph = JSON.parse(
              await storedRules.Body.transformToString(),
            );
            set_graph(stored_graph);
            set_graph_dirty(false);
            set_load_complete(false);
            debounced_set_load_complete();
          }
          break;
        }
      }
    })();
  }, [route_suffix, set_graph, storedRules, debounced_set_load_complete]);

  useEffect(
    () =>
      set_page_title(`ERP GoRules ${route_suffix ? " - " + route_suffix : ""}`),
    [set_page_title, route_suffix],
  );

  const { mutateAsync: saveS3File } = useSaveS3File(s3_access?.s3_client);
  const save = async () => {
    if (!new_name && route_suffix === "new") {
      set_show_new_name_modal(true);
    } else {
      await saveS3File({
        key: new_name ?? route_suffix,
        content: JSON.stringify(graph),
      });
      set_graph_dirty(false);
      set_show_new_name_modal(false);
      if (route_suffix === "new") {
        navigate(`/erp/gorules/${new_name}`);
      }
    }
  };

  return (
    <Flex h="100%" direction="column">
      <Flex w="100%" flexGrow={1} align="beginning">
        <VStack align="end" textAlign="right" pr={4}>
          <Heading as="h4" size="lg">
            Rule&nbsp;Sets
          </Heading>
          {gorules_objs?.Contents?.map((obj) => (
            <ChakraLink
              as={Link}
              fontSize="xl"
              key={obj.Key}
              to={`/erp/gorules/${obj.Key}`}
            >
              {obj.Key}
            </ChakraLink>
          ))}
          <ChakraLink as={Link} px={6} to="/erp/gorules/new">
            <PlusIcon />
          </ChakraLink>
        </VStack>
        <VStack align="end" textAlign="right" pr={4} flexGrow="1" h="100%">
          <Box h="4ex">
            {graph_dirty && (
              <Button background="green.400" onClick={save}>
                Save
              </Button>
            )}
          </Box>
          <Box w="100%" h="100%">
            {graph && (
              <JdmConfigProvider>
                <DecisionGraph
                  value={graph}
                  onChange={(g) => {
                    set_graph(g);
                    if (load_complete) {
                      set_graph_dirty(true);
                    }
                  }}
                />
              </JdmConfigProvider>
            )}
          </Box>
          <Modal
            isOpen={show_new_name_modal}
            onClose={() => set_show_new_name_modal(false)}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Center>New GoRules Rules Name</Center>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack>
                  <FormControl>
                    <FormLabel htmlFor="new-rules-name">
                      <b>Name</b>
                    </FormLabel>
                    <Input
                      id="new-rules-name"
                      value={new_name}
                      onChange={(e) => set_new_name(e.target.value)}
                    />
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button
                  background="green.400"
                  onClick={save}
                  disabled={!new_name}
                >
                  Save
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </VStack>
      </Flex>
    </Flex>
  );
};

export default GoRules;
